import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import FinanceLayout from 'src/feature/finance/FinanceLayout'
import {calculateLoanMetrics, parseAnnualRate} from '../business-loan/loanCalculator'
import Card1 from '../finance/assets/card-1.jpg'
import Card2 from '../finance/assets/card-2.jpg'
import Card3 from '../finance/assets/card-3.jpg'
import {BreadCrumb} from 'src/_metronic/layout/components'

const CARD_OFFERS = [
  {
    id: '1',
    bankName: 'Demo Bank',
    image: Card1,
    annualRate: parseAnnualRate('12.55%'),
    reward: '3.00% Cashback',
    annualFee: '$0',
    introApr: '0%',
    features: ['$200 welcome bonus after $1,000 spend', 'Complimentary airport lounge access'],
    earningsType: 'Cashback',
    hasAnnualFee: false,
    opportunityType: 'Spending Discount',
    spendingAreas: ['Fuel & Automotive', 'E-Commerce'],
  },
  {
    id: '2',
    bankName: 'My Bank',
    image: Card2,
    annualRate: parseAnnualRate('12.55%'),
    reward: '2x Travel Points',
    annualFee: '$95',
    introApr: '14.9%',
    features: ['No foreign transaction fees', '24/7 concierge service'],
    earningsType: 'Miles',
    hasAnnualFee: true,
    opportunityType: 'Installment Cash Advance',
    spendingAreas: ['Cafe & Restaurant', 'Grocery & Market'],
  },
  {
    id: '3',
    bankName: 'My Bank',
    image: Card3,
    annualRate: parseAnnualRate('12.55%'),
    reward: '1.5% Cashback',
    annualFee: '$0',
    introApr: '0%',
    features: ['Mobile app expense tracking', 'Instant digital card issuance'],
    earningsType: 'Points',
    hasAnnualFee: false,
    opportunityType: 'Spending Discount',
    spendingAreas: ['E-Commerce', 'Education & Stationery'],
  },
]

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatRate(value: number) {
  return `${(value * 100).toFixed(2)}%`
}

const FilterSection: React.FC<{
  title: string
  options: string[]
  selected: string[]
  onChange: (val: string[]) => void
  hasSearch?: boolean
}> = ({title, options, selected, onChange, hasSearch}) => {
  const [isOpen, setIsOpen] = React.useState(true)
  const [search, setSearch] = React.useState('')

  const filteredOptions = options.filter((opt) => opt.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className='border-bottom border-gray-200 pb-4 mb-4'>
      <button
        className='btn btn-link text-gray-800 fw-bold fs-7 p-0 w-100 d-flex justify-content-between align-items-center mb-4 text-decoration-none'
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} fs-9`} />
      </button>

      {isOpen && (
        <div className='d-flex flex-column gap-3'>
          {hasSearch && (
            <div className='position-relative mb-2'>
              <i className='bi bi-search position-absolute top-50 start-0 translate-middle-y ms-4 text-gray-500' />
              <input
                type='text'
                className='form-control form-control-solid ps-10 py-2'
                placeholder='Search...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <div className='max-h-200px overflow-auto pe-2 d-flex gap-3'>
            {filteredOptions.map((option) => (
              <label
                key={option}
                className='form-check form-check-custom form-check-solid form-check-sm'
              >
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={selected.includes(option)}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...selected, option])
                    else onChange(selected.filter((s) => s !== option))
                  }}
                />
                <span className='form-check-label text-gray-700 fw-semibold fs-7'>{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const BusinessCardPage: React.FC = () => {
  const navigate = useNavigate()

  const [sortBy, setSortBy] = useState<'reward' | 'rate'>('reward')

  // New Filter States
  const [earningsTypes, setEarningsTypes] = useState<string[]>([])
  const [annualFeeFilter, setAnnualFeeFilter] = useState<string[]>([])
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([])
  const [spendingAreas, setSpendingAreas] = useState<string[]>([])
  const [selectedBanks, setSelectedBanks] = useState<string[]>([])

  const filteredOffers = CARD_OFFERS.filter((offer) => {
    if (earningsTypes.length > 0 && !earningsTypes.includes(offer.earningsType)) return false
    if (annualFeeFilter.length > 0) {
      if (annualFeeFilter.includes('Var') && !offer.hasAnnualFee) return false
      if (annualFeeFilter.includes('Yok') && offer.hasAnnualFee) return false
    }
    if (opportunityTypes.length > 0 && !opportunityTypes.includes(offer.opportunityType))
      return false
    if (
      spendingAreas.length > 0 &&
      !offer.spendingAreas.some((area) => spendingAreas.includes(area))
    )
      return false
    if (selectedBanks.length > 0 && !selectedBanks.includes(offer.bankName)) return false
    return true
  })

  const offersToShow = [...filteredOffers].sort((left, right) => {
    if (sortBy === 'rate') {
      return left.annualRate - right.annualRate
    }
    return 0
  })

  return (
    <>
      <div className='d-flex gap-5 align-items-center mb-10'>
        <button
          className='btn btn-primary btn-sm btn-back'
          onClick={() => {
            navigate('/finance')
          }}
        >
          <i className='fa fa-arrow-left'></i>
        </button>
        <BreadCrumb currentPage={'Business Card'} />
      </div>
      <div className='row g-6'>
        <div className='col-xl-8'>
          <div className='d-flex flex-column gap-6'>
            {offersToShow.map((offer) => (
              <div key={offer.id} className='card border border-gray-200 shadow-sm'>
                <div className='card-body p-7'>
                  <div className='row g-6 align-items-center'>
                    <div className='col-lg-3 d-flex flex-column align-items-center align-items-lg-center text-center text-lg-start'>
                      <div className='w-100 d-flex justify-content-center justify-content-lg-center mb-3'>
                        <img
                          src={offer.image}
                          alt={offer.bankName}
                          className='rounded-3'
                          style={{height: '120px', maxWidth: '100%', objectFit: 'contain'}}
                        />
                      </div>
                      <h3 className='fw-bold fs-4 text-gray-900 mb-2'>{offer.bankName}</h3>
                    </div>

                    <div className='col-lg-6 mt-0'>
                      <div className='row g-4 mb-6'>
                        <div className='col-sm-4'>
                          <span className='text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                            Reward Rate
                          </span>
                          <div className='fw-bold fs-2 text-gray-900'>{offer.reward}</div>
                        </div>
                        <div className='col-sm-4'>
                          <span className='text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                            Annual Fee
                          </span>
                          <div className='fw-bold fs-2 text-gray-900'>{offer.annualFee}</div>
                        </div>
                        <div className='col-sm-4'>
                          <span className='text-uppercase fw-bold fs-8 d-block mb-1 text-secondary'>
                            Intro APR
                          </span>
                          <div className='fw-bold fs-2 text-gray-900'>{offer.introApr}</div>
                        </div>
                      </div>

                      <div className=''>
                        <div className='d-flex flex-column gap-3'>
                          {offer.features.map((feature) => (
                            <div key={feature} className='d-flex align-items-start gap-3'>
                              <i className='bi bi-check-circle-fill text-success mt-1' />
                              <span className='fw-semibold'>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className='col-lg-3'>
                      <div className='d-flex flex-column justify-content-center align-items-center gap-3'>
                        <button
                          className='btn btn-primary w-50'
                          onClick={() => navigate('/finance/business-card/detail?mode=apply')}
                        >
                          Apply Now
                        </button>
                        <button
                          className='btn btn-text w-100'
                          onClick={() => navigate('/finance/business-card/detail')}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='col-xl-4'>
          <div className='card border border-gray-200 shadow-sm sticky-top'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bold text-secondary'>Filters</h3>
            </div>
            <div className='card-body pt-0 d-flex flex-column'>
              <FilterSection
                title='Reward Types'
                options={['Cashback', 'Discount', 'Points', 'Miles']}
                selected={earningsTypes}
                onChange={setEarningsTypes}
              />
              <FilterSection
                title='Annual Fee'
                options={['Yes', 'No']}
                selected={annualFeeFilter}
                onChange={setAnnualFeeFilter}
              />
              <FilterSection
                title='Offer Type'
                options={['Installment Cash Advance', 'Spending Discount']}
                selected={opportunityTypes}
                onChange={setOpportunityTypes}
              />

              <FilterSection
                title='Banks'
                options={['Demo Bank', 'My Bank']}
                selected={selectedBanks}
                onChange={setSelectedBanks}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default BusinessCardPage
